// frontend/src/components/chat/ChatPanel.jsx
// PHASE 5 UPDATE: Added search functionality
// Slide-out chat panel container with settings and search

import React, { useState } from "react";
import { useChat } from "../../contexts/ChatContext";
import ConversationList from "./ConversationList";
import ChatWindow from "./ChatWindow";
import NotificationSettings from "./NotificationSettings";
import ChatSearch from "./ChatSearch";
import notificationService from "../../services/notificationService";
import "../../assets/css/chat.css";

const ChatPanel = () => {
  const {
    isOpen,
    closeChat,
    activeConversation,
    closeConversation,
    openConversation,
    notificationsEnabled,
    requestNotificationPermission,
  } = useChat();

  const [showSettings, setShowSettings] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showPermissionBanner, setShowPermissionBanner] = useState(() => {
    // Show banner if notifications are supported but not enabled
    const status = notificationService.getStatus();
    return status.isSupported && status.permission === "default";
  });

  // Handle enabling notifications from banner
  const handleEnableNotifications = async () => {
    const granted = await requestNotificationPermission();
    if (granted) {
      setShowPermissionBanner(false);
    }
  };

  // Dismiss permission banner
  const dismissPermissionBanner = () => {
    setShowPermissionBanner(false);
    // Remember dismissal for this session
    sessionStorage.setItem("chat_notification_banner_dismissed", "true");
  };

  // Handle navigation from search results
  const handleSearchNavigate = (conversationId, messageId) => {
    // Open the conversation and scroll to the specific message
    openConversation(conversationId, { scrollToMessageId: messageId });
    setShowSearch(false);
  };

  // Close search
  const handleCloseSearch = () => {
    setShowSearch(false);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop for mobile */}
      <div
        className="chat-backdrop d-md-none"
        onClick={closeChat}
        aria-hidden="true"
      />

      {/* Chat Panel */}
      <div className={`chat-panel ${isOpen ? "open" : ""}`}>
        {/* Header */}
        <div className="chat-panel-header">
          <div className="d-flex align-items-center">
            {activeConversation && !showSearch && (
              <button
                className="btn btn-link chat-back-btn p-0 me-2"
                onClick={() => {
                  closeConversation();
                  setShowSettings(false);
                }}
                aria-label="Back to conversations"
              >
                <i className="bi bi-arrow-left"></i>
              </button>
            )}
            {showSettings && !activeConversation && !showSearch && (
              <button
                className="btn btn-link chat-back-btn p-0 me-2"
                onClick={() => setShowSettings(false)}
                aria-label="Back to conversations"
              >
                <i className="bi bi-arrow-left"></i>
              </button>
            )}
            {showSearch && (
              <button
                className="btn btn-link chat-back-btn p-0 me-2"
                onClick={handleCloseSearch}
                aria-label="Close search"
              >
                <i className="bi bi-arrow-left"></i>
              </button>
            )}
            <h6 className="mb-0">
              {showSearch
                ? "Search"
                : showSettings
                ? "Settings"
                : activeConversation
                ? activeConversation.other_participant?.name || "Chat"
                : "Messages"}
            </h6>
          </div>
          <div className="d-flex align-items-center gap-2">
            {/* Search Button - show when not in settings/search */}
            {!showSettings && !showSearch && (
              <button
                className="btn btn-link chat-search-btn p-0"
                onClick={() => setShowSearch(true)}
                aria-label="Search messages"
                title="Search messages"
              >
                <i className="bi bi-search"></i>
              </button>
            )}
            {/* Settings Button - only show when not in settings/search */}
            {!showSettings && !activeConversation && !showSearch && (
              <button
                className="btn btn-link chat-settings-btn p-0"
                onClick={() => setShowSettings(true)}
                aria-label="Chat settings"
                title="Settings"
              >
                <i className="bi bi-gear"></i>
              </button>
            )}
            <button
              className="btn btn-link chat-close-btn p-0"
              onClick={closeChat}
              aria-label="Close chat"
            >
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
        </div>

        {/* Notification Permission Banner */}
        {showPermissionBanner &&
          !showSettings &&
          !showSearch &&
          !activeConversation &&
          !sessionStorage.getItem("chat_notification_banner_dismissed") && (
            <div className="notification-permission-banner">
              <div className="banner-text">
                <i className="bi bi-bell"></i>
                Enable notifications to get alerted about new messages
              </div>
              <button
                className="btn btn-primary btn-sm"
                onClick={handleEnableNotifications}
              >
                Enable
              </button>
              <button
                className="btn btn-link btn-sm text-muted p-0 ms-2"
                onClick={dismissPermissionBanner}
                aria-label="Dismiss"
              >
                <i className="bi bi-x"></i>
              </button>
            </div>
          )}

        {/* HIPAA Warning Banner - hide during search */}
        {!showSearch && (
          <div className="chat-hipaa-banner">
            <i className="bi bi-shield-lock me-1"></i>
            <small>
              HIPAA Notice: This chat may contain PHI. Only discuss when
              necessary.
            </small>
          </div>
        )}

        {/* Content */}
        <div className="chat-panel-content">
          {showSearch ? (
            <ChatSearch
              onClose={handleCloseSearch}
              onNavigate={handleSearchNavigate}
            />
          ) : showSettings ? (
            <NotificationSettings onClose={() => setShowSettings(false)} />
          ) : activeConversation ? (
            <ChatWindow />
          ) : (
            <ConversationList />
          )}
        </div>
      </div>
    </>
  );
};

export default ChatPanel;
